/**
 * CR-5534: Reusable sub-components for the Approval Register matrix.
 *
 * Extracted from ApprovalRegister.tsx to keep that file under the
 * 500-line budget. Covers the matrix cell button (RAG dot + tooltip)
 * and the certificate View / Download actions (which need an extra detail
 * fetch for the file name because the matrix endpoint only carries
 * `has_certificate`).
 */
import { useEffect, useState } from 'react';
import { Circle, Clock, Download, Eye, Loader2 } from 'lucide-react';

import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { useAccreditationCertificate } from '../components/quality/useAccreditationCertificate';
import { qualityAPI } from '../lib/api';
import type {
  AccreditationStatus,
  ApprovalRegisterCell,
  ApprovalRegisterPending,
} from '../lib/api';

/**
 * Matrix dot treatment per status.
 *
 * CR-7171: `no_expiry` used to carry the same `#22C55E` as `in_date`, which
 * made a certificate nobody had dated pixel-identical to one checked and known
 * to be current — on an aerospace approval register, the exact confusion the
 * register exists to prevent. It is now drawn as a hollow ring: unfilled, so it
 * reads as "no status recorded" rather than a fourth colour competing with the
 * three-tier expiry scale.
 */
/**
 * CR-7171: the two things a blank expiry can mean, worded once. Imported by
 * every surface that renders the state so the matrix, the legend and the
 * supplier profile cannot drift into three phrasings of the same fact.
 */
export const NO_EXPIRY_RECORDED_LABEL = 'No expiry recorded';
export const NO_EXPIRY_DELIBERATE_LABEL = 'Does not expire';

export const STATUS_STYLES: Record<
  AccreditationStatus,
  { fill: string; stroke: string; label: string }
> = {
  in_date: { fill: '#22C55E', stroke: '#22C55E', label: 'In date' },
  expiring: { fill: '#F59E0B', stroke: '#F59E0B', label: 'Expiring soon' },
  expired: { fill: '#EF4444', stroke: '#EF4444', label: 'Expired' },
  no_expiry: { fill: 'none', stroke: '#64748B', label: NO_EXPIRY_RECORDED_LABEL },
};

interface MatrixCellProps {
  cell: ApprovalRegisterCell | undefined;
  expiryFormat: (d: string) => string;
  canAdd: boolean;
  /** CR-7750: an open supplier submission against this cell, if any. */
  pending?: ApprovalRegisterPending | null;
  onClick: () => void;
}

/**
 * CR-7750: the "something is waiting" mark.
 *
 * A small clock badge in the corner, NOT a change to the dot. The dot is the
 * approved status, and a submission must not alter it — that is the whole
 * point of holding submissions for review.
 */
function PendingMark() {
  return (
    <span
      className="absolute -top-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-900"
      aria-label="Awaiting review"
    >
      <Clock className="h-2 w-2 text-white" />
    </span>
  );
}

export function MatrixCell({
  cell, expiryFormat, canAdd, pending, onClick,
}: MatrixCellProps) {
  if (!cell) {
    return (
      <span className="relative inline-block">
        <button
          onClick={onClick}
          disabled={!canAdd}
          className={`inline-block w-6 h-6 rounded-full border border-dashed border-gray-400 ${
            canAdd ? 'hover:bg-gray-100' : 'cursor-default opacity-60'
          }`}
          title={
            pending ? 'Submitted — awaiting review'
              : canAdd ? 'Add accreditation' : 'Not held'
          }
        />
        {pending && <PendingMark />}
      </span>
    );
  }
  const style = STATUS_STYLES[cell.status];
  // CR-7171: a ticked "no expiry applies" is a statement about the
  // certificate; a bare blank is a gap in the register. Same dot, different
  // sentence — the reader should be able to tell which one they are looking at.
  const statusLabel =
    cell.status === 'no_expiry' && cell.no_expiry_applies
      ? NO_EXPIRY_DELIBERATE_LABEL
      : style.label;
  const tooltipLines = [
    statusLabel,
    cell.expiry_date ? `Expires ${expiryFormat(cell.expiry_date)}` : null,
    cell.certificate_number ? `Cert #${cell.certificate_number}` : null,
    pending ? 'A supplier submission is awaiting review' : null,
  ].filter(Boolean) as string[];

  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className="relative p-1 rounded-full hover:bg-muted/50 transition-colors"
          >
            <Circle
              className="h-6 w-6"
              fill={style.fill}
              stroke={style.stroke}
              strokeWidth={2.5}
            />
            {pending && <PendingMark />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-0.5 text-xs">
            {tooltipLines.map((l) => (
              <div key={l}>{l}</div>
            ))}
            <div className="italic opacity-70">Click for details</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function LegendItem({
  fill,
  stroke,
  label,
}: {
  fill: string;
  stroke: string;
  label: string;
}) {
  return (
    <span className="flex items-center gap-2 text-sm">
      <Circle className="h-4 w-4" fill={fill} stroke={stroke} />
      {label}
    </span>
  );
}

export function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

/**
 * CR-7747: the certificate's View / Download actions in the cell popup.
 *
 * The matrix cell only says a certificate exists, so one detail fetch gets
 * the file name; the file itself comes from the authenticated download
 * endpoint. The name is only a label — if that fetch fails the actions still
 * work and the file is saved as "certificate".
 */
export function DetailCertificateActions({ bindingId }: { bindingId: number }) {
  const [loading, setLoading] = useState(true);
  const [filename, setFilename] = useState('');
  const { pending, run } = useAccreditationCertificate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    qualityAPI
      .getSupplierAccreditation(bindingId)
      .then((res) => {
        if (!cancelled) setFilename(res.data.certificate_file_name || '');
      })
      .catch(() => {
        if (!cancelled) setFilename('');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bindingId]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading certificate…</div>;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="shrink-0 text-muted-foreground">Certificate File</span>
        <span className="min-w-0 break-words font-medium text-right">{filename || 'Attached'}</span>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          disabled={pending !== null}
          onClick={() => run(bindingId, filename, 'view')}
        >
          {pending === 'view' ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Eye className="h-4 w-4 mr-2" />
          )}
          View Certificate
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={pending !== null}
          onClick={() => run(bindingId, filename, 'download')}
        >
          {pending === 'download' ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download
        </Button>
      </div>
    </div>
  );
}
